import React from "react";
import styles from "./styles.module.scss";
import { useTranslation } from "react-i18next";
import RemainingSessions from "../RemainingSessions";
import { weekdayRestrictionLabel } from "../../../NewOffering/utils";
import { DateTime } from "luxon";
import { formatMonthDayWithYearIfNeeded } from "app/utils/luxonFormats";

interface RateLink {
  title: string;
  quantity: number;
  purchaseId: string;
  remainingSessions: number;
  id: number;
}

interface Offering {
  offeringTitle: string;
  rateLinks: RateLink[];
  productIntegerId: string;
  restrictWeekdays?: boolean;
  restrictWeekends?: boolean;
  weekdayBeginAt?: number;
  weekdayEndAt?: number;
  // CN-2641: when this package's credits expire; null when they never do.
  expiredAt?: string | null;
}

interface OfferingCardWithRatesProps {
  subscription: Offering;
  updateRemainingSessions?: (
    purchaseId: string,
    incrementValue: number
  ) => void;
  isCoach?: boolean;
  purchaseId?: string;
  studentId?: string;
}

export default function OfferingCardWithRates({
  subscription,
  updateRemainingSessions,
  isCoach = false,
  purchaseId,
}: OfferingCardWithRatesProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.subscriptionCardCoach}>
      <div className={styles.topSection}>
        <div className={styles.iconWrapper2}>
          <i className="tab-icon ico ico-dollar-circle" />
        </div>

        <div className={styles.infoBlock}>
          <p className={styles.subscriptionName}>{subscription.offeringTitle}</p>
          <div className={styles.subscriptionRow}>
            <p className={styles.subscriptionDetails}>{t("One time payment")}</p>
            {/* CN-2641: a package with a relative expiry has a known last day. */}
            {subscription.expiredAt && (
              <>
                <span className={styles.dotSeparator}>•</span>
                <p className={styles.subscriptionDetails}>
                  {t("Credits expire on {{date}}", {
                    date: formatMonthDayWithYearIfNeeded(
                      DateTime.fromISO(subscription.expiredAt),
                    ),
                  })}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {subscription.restrictWeekdays && !subscription.restrictWeekends && (
        <div className={styles.restriction_area}>
          <span className={styles.restriction_badge}>
            {weekdayRestrictionLabel(
              subscription.weekdayBeginAt ?? 0,
              subscription.weekdayEndAt ?? 1440,
              t("Weekdays only"),
            )}
          </span>
        </div>
      )}
      {subscription.restrictWeekends && !subscription.restrictWeekdays && (
        <div className={styles.restriction_area}>
          <span className={styles.restriction_badge}>
            {t("Weekends & Holidays only")}
          </span>
        </div>
      )}

      {isCoach && subscription.rateLinks?.length > 0 && (
        <>
          <div className={styles.dividerLine} />

          <div className={styles.rate_links_container_coach}>
            {subscription.rateLinks.map((link, idx) => (
              <div className={styles.rate_link_row_coach} key={idx}>
                <div className={styles.rate_link_left_coach}>
                  <span className={styles.rate_link_title_coach}>{link.title}</span>
                  <span className={styles.rate_link_description}>
                    {t("Here you can set amount of remaining sessions for this athlete")}
                  </span>
                </div>
                <RemainingSessions
                  remaining={link.remainingSessions}
                  unlimited={false}
                  updateRemainingSessions={updateRemainingSessions}
                  purchaseId={purchaseId}
                  rateLinkId={link.id}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
